import DeleteModal from "components/DeleteModal";
import { useTooltip } from "context/TooltipProvider";
import SuspectDetailInfo from "pages/Incident/IncidentDetail/modals/SuspectDetailInfo";
import WitnessOnlyDetail from "pages/Incident/IncidentDetail/modals/WitnessOnlyDetail";
import { FC, useState } from "react";
import { deleteWitnessDefendants } from "services/caseServices";
import {
  CaseCreation,
  Exhibit,
  Reporter,
  Suspect,
  Victim,
  Witness,
  WitnessDefendants,
} from "types";
import { errorHandler } from "utils/helper";
import WitnessDefendantForm from "./WitnessDefendantForm";

type Props = {
  caseData: CaseCreation;
  caseRelatedParties: {
    suspects: Suspect[];
    witnesses: Witness[];
    victims: Victim[];
    reporters: Reporter[];
    exhibits: Exhibit[];
  };
  fetchData: () => void;
  isCoordinator?: boolean;
  isRegistrar?: boolean;
  isProsecutor?: boolean;
};

const WitnessList: FC<Props> = ({
  caseData,
  caseRelatedParties,
  fetchData,
  isCoordinator = false,
  isRegistrar = false,
  isProsecutor = false,
}) => {
  const { showTooltip } = useTooltip();
  const [openForm, setOpenForm] = useState(false);
  const [selectedDefendant, setSelectedDefendant] = useState<Suspect>();
  const [isOpenDefendantDetail, setIsOpenDefendantDetail] = useState(false);
  const [selectedWitness, setSelectedWitness] = useState<Suspect>();
  const [isOpenWitnessDetail, setIsOpenWitnessDetail] = useState(false);
  const [isOpenDelete, setOpenDelete] = useState(false);
  const [selectedData, setSelectedData] = useState<WitnessDefendants>();
  const [action, setAction] = useState<"add" | "edit">("add");
  const [openAccordion, setOpenAccordion] = useState<number | null>(0);

  const handleAccordionToggle = (idx: number) => {
    setOpenAccordion((prev) => (prev === idx ? null : idx));
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-2 h-8 bg-red-600 rounded-full"></div>
            <h3 className="text-lg font-semibold text-gray-900">Witnesses</h3>
          </div>

          {isProsecutor &&
            (caseData?.status === "Pending" ||
              caseData?.status === "Respond_Back_prosecutor") && (
              <button
                type="button"
                className="px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                onClick={() => setOpenForm(true)}
              >
                + Add Witness
              </button>
            )}
        </div>

        {caseData?.witnesses &&
        Array.isArray(caseData.witnesses) &&
        caseData.witnesses.length > 0 ? (
          <div className="space-y-4">
            {caseData.witnesses.map((w: any, idx: number) => {
              const isOpen = openAccordion === idx;
              return (
                <div
                  key={w.id || idx}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <button
                    className="w-full flex justify-between items-center px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                    onClick={() => handleAccordionToggle(idx)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="font-semibold text-green-600">W</span>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">
                          {w.witnessed_info?.first_name}{" "}
                          {w.witnessed_info?.middle_name}{" "}
                          {w.witnessed_info?.last_name}
                        </h3>
                        <div className="text-xs text-gray-500">
                          {w.witnessed_info?.gender || "N/A"} •{" "}
                          {w.witnessed_info?.phone_number || "N/A"}
                        </div>
                      </div>
                    </div>
                    <svg
                      className={`w-5 h-5 text-gray-500 transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {isOpen && (
                    <div className="p-6 space-y-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {w.witnessed_info?.first_name}{" "}
                            {w.witnessed_info?.middle_name}{" "}
                            {w.witnessed_info?.last_name}
                          </h4>
                          <div className="text-sm text-gray-600 mt-1">
                            {w.witnessed_info?.statement ||
                              "No statement provided"}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedWitness(w?.witnessed_info);
                              setIsOpenWitnessDetail(true);
                            }}
                            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                          >
                            View Details
                          </button>

                          {(caseData?.status === "Pending" ||
                            caseData?.status === "Respond_Back_prosecutor" ||
                            caseData.is_case_updated_by_court) && (
                            <button
                              onClick={() => {
                                setSelectedData(w);
                                setAction("edit");
                                setOpenForm(true);
                              }}
                              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                              Edit
                            </button>
                          )}

                          {(caseData?.status === "Pending" ||
                            caseData?.status === "Respond_Back_prosecutor") && (
                            <button
                              onClick={() => {
                                setSelectedData(w);
                                setOpenDelete(true);
                              }}
                              className="px-4 py-2 text-sm text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>

                      {w.witnessed_defendants_info?.length > 0 && (
                        <div className="border-t border-gray-200 pt-4">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3">
                            Related Defendants
                          </h4>
                          <div className="space-y-2">
                            {w.witnessed_defendants_info.map(
                              (d: any, dIdx: number) => (
                                <div
                                  key={dIdx}
                                  className="bg-gray-50 rounded-lg p-3"
                                >
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <h5 className="font-medium text-sm text-gray-900">
                                        {d.defendant_info?.first_name}{" "}
                                        {d.defendant_info?.middle_name}{" "}
                                        {d.defendant_info?.last_name}
                                      </h5>
                                      <div className="text-xs text-gray-600 mt-1">
                                        {d.defendant_info?.gender || "N/A"} •{" "}
                                        {d.defendant_info?.phone_number ||
                                          "N/A"}
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => {
                                        setSelectedDefendant(d.defendant_info);
                                        setIsOpenDefendantDetail(true);
                                      }}
                                      className="px-3 py-1 text-xs text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                                    >
                                      View
                                    </button>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No witnesses found
          </div>
        )}
      </div>

      {/* Modals */}
      {openForm && caseRelatedParties && caseData && (
        <WitnessDefendantForm
          caseData={caseData}
          action={action}
          selectedData={selectedData}
          caseRelatedParties={caseRelatedParties}
          onClose={(reload) => {
            setOpenForm(false);
            setSelectedData(undefined);
            if (reload) fetchData();
          }}
        />
      )}

      {isOpenDefendantDetail && (
        <SuspectDetailInfo
          suspect={selectedDefendant}
          isOpen={isOpenDefendantDetail}
          onClose={() => setIsOpenDefendantDetail(false)}
        />
      )}

      {isOpenWitnessDetail && (
        <WitnessOnlyDetail
          witness={selectedWitness}
          isOpen={isOpenWitnessDetail}
          onClose={() => setIsOpenWitnessDetail(false)}
        />
      )}

      {selectedData && (
        <DeleteModal
          isOpen={isOpenDelete}
          onConfirm={() => {
            deleteWitnessDefendants(selectedData?.id)
              .then(() => {
                showTooltip("Successfully Deleted!", "success");
                fetchData();
              })
              .catch((error) => {
                showTooltip(
                  error?.response?.data?.detail || errorHandler(error)
                );
              })
              .finally(() => {
                setOpenDelete(false);
                setSelectedData(undefined);
              });
          }}
          onCancel={() => {
            setOpenDelete(false);
            setSelectedData(undefined);
          }}
          title="Delete Witness"
          message="Are you sure you want to delete witness data?"
        />
      )}
    </div>
  );
};

export default WitnessList;
